import React, { useEffect, useMemo, useState } from 'react';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import OperationStatus from './OperationStatus';

const PAGE_SIZE = 20;

function formatValue(value, fallback = 'Não informado') {
  return String(value || '').trim() || fallback;
}

function formatSource(source) {
  const labels = {
    bling: 'Bling',
    auvo: 'Auvo',
    taskhub: 'Task Hub',
    manual: 'Manual',
    cec_integrated: 'CEC Integrado'
  };
  return labels[source] || formatValue(source, 'Origem');
}

function formatDate(value) {
  if (!value) return 'Não informado';
  return new Date(value).toLocaleString('pt-BR');
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })}%`;
}

function originSummary(origins = []) {
  if (!origins.length) return 'Sem vínculos';
  return origins.map(formatSource).join(', ');
}

function masterIdFromLink(link) {
  return link?.masterClient?._id || link?.masterClient?.id || link?.masterClientId || '';
}

function emptySearchResults() {
  return {
    masterClients: [],
    blingClients: [],
    auvoClients: [],
    linkedRecords: []
  };
}

function externalKey(record) {
  return `${record.sourceSystem}:${record.importedClientId || record.id}:${record.externalId || ''}`;
}

const COMPLETION_FIELDS = [
  ['name', 'nome'],
  ['corporateName', 'razão social'],
  ['fantasyName', 'nome fantasia'],
  ['documentNumber', 'CNPJ'],
  ['phone', 'telefone'],
  ['email', 'e-mail'],
  ['address', 'endereço'],
  ['city', 'cidade'],
  ['state', 'estado']
];

const COMPARISON_FIELDS = [
  ['name', 'Nome'],
  ['corporateName', 'Razão social'],
  ['fantasyName', 'Nome fantasia'],
  ['documentNumber', 'CNPJ'],
  ['stateRegistration', 'Inscrição estadual'],
  ['phone', 'Telefone'],
  ['mobile', 'Celular'],
  ['email', 'E-mail'],
  ['zipCode', 'CEP'],
  ['address', 'Endereço'],
  ['number', 'Número'],
  ['district', 'Bairro'],
  ['city', 'Cidade'],
  ['state', 'Estado']
];

export default function ClientConsolidationPanel({ pageMode = false, onClose, onUnauthorized }) {
  const [summary, setSummary] = useState({ dashboard: null });
  const [suggestions, setSuggestions] = useState([]);
  const [mastersPayload, setMastersPayload] = useState({ rows: [], page: 1, totalPages: 1, total: 0 });
  const [linksPayload, setLinksPayload] = useState({ rows: [], page: 1, totalPages: 1, total: 0 });
  const [documentMismatchReport, setDocumentMismatchReport] = useState({ rows: [], total: 0 });
  const [activeTab, setActiveTab] = useState('masters');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('consolidate');
  const [filters, setFilters] = useState({ q: '', status: '', origin: '' });
  const [pages, setPages] = useState({ masters: 1, links: 1 });
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [showLoadStatus, setShowLoadStatus] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedAuditCandidateKeys, setSelectedAuditCandidateKeys] = useState([]);
  const [dataComparison, setDataComparison] = useState(null);
  const [comparisonUpdates, setComparisonUpdates] = useState({});
  const [backfillResult, setBackfillResult] = useState(null);
  const [unifiedQuery, setUnifiedQuery] = useState('');
  const [unifiedResults, setUnifiedResults] = useState(() => emptySearchResults());
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [selectedExternalKeys, setSelectedExternalKeys] = useState([]);
  const [completionFields, setCompletionFields] = useState({});
  const [mergeSourceMaster, setMergeSourceMaster] = useState(null);

  const orderedSuggestions = useMemo(
    () => [...suggestions].sort((a, b) => b.confidenceScore - a.confidenceScore),
    [suggestions]
  );
  const dashboard = summary.dashboard || {};
  const sourceDistribution = dashboard.sourceDistribution || {};
  const sourceReferences = dashboard.sourceReferences || {};
  const consolidation = dashboard.consolidation || {};
  const dataQuality = dashboard.dataQuality || {};
  const rates = dashboard.rates || {};
  const sourceHealth = dashboard.sourceHealth || {};
  const sourceCoverage = dashboard.sourceCoverage || {};
  const allExternalResults = useMemo(
    () => [...(unifiedResults.blingClients || []), ...(unifiedResults.auvoClients || [])],
    [unifiedResults]
  );
  const selectedExternalRecords = useMemo(
    () => allExternalResults.filter((record) => selectedExternalKeys.includes(externalKey(record))),
    [allExternalResults, selectedExternalKeys]
  );

  const handleApiError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized();
      return;
    }
    setError(getErrorMessage(requestError, fallbackMessage));
  };

  const loadDashboard = async () => {
    const payload = await ClientConsolidationAPI.suggestions();
    setSummary(payload.summary || {});
    setSuggestions(payload.suggestions || []);
  };

  const runUnifiedSearch = async (event) => {
    event?.preventDefault();
    if (!unifiedQuery.trim()) return;
    setUnifiedLoading(true);
    setError('');
    try {
      const payload = await ClientConsolidationAPI.search(unifiedQuery);
      setUnifiedResults(payload || emptySearchResults());
      setSelectedMasterId(payload?.masterClients?.[0]?.id || '');
      setSelectedExternalKeys([]);
      setCompletionFields({});
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível buscar cadastros em Cliente Mestre, Bling e Auvo.');
    } finally {
      setUnifiedLoading(false);
    }
  };

  const toggleExternalRecord = (record) => {
    const key = externalKey(record);
    setSelectedExternalKeys((current) => (
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    ));
  };

  const buildExternalPayload = () => selectedExternalRecords.map((record) => ({
    sourceSystem: record.sourceSystem,
    externalId: record.externalId,
    importedClientId: record.importedClientId || record.id
  }));

  const buildFieldSelections = () => Object.fromEntries(
    Object.entries(completionFields)
      .filter(([, selected]) => selected?.enabled && selected?.value)
      .map(([field, selected]) => [field, selected])
  );

  const detectDocumentMismatch = (masterId = selectedMasterId) => {
    const selectedMaster = unifiedResults.masterClients?.find((master) => master.id === masterId);
    if (!selectedMaster?.documentNumber) return false;
    return selectedExternalRecords.some((record) => {
      if (!record.documentNumber) return false;
      return String(record.documentNumber).replace(/\D+/g, '') !== String(selectedMaster.documentNumber).replace(/\D+/g, '');
    });
  };

  const handleLinkSelected = async () => {
    if (!selectedMasterId || !selectedExternalRecords.length) return;
    if (detectDocumentMismatch()) {
      setError('Os CNPJs são diferentes. Deseja criar grupo econômico ou manter como clientes separados?');
      return;
    }
    setActionId('link-selected');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.linkSelected({
        masterClientId: selectedMasterId,
        externalRecords: buildExternalPayload()
      });
      if (payload.blocked?.length) {
        setError('Os CNPJs são diferentes. Deseja criar grupo econômico ou manter como clientes separados?');
      }
      await Promise.all([runUnifiedSearch(), loadAll()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível vincular os cadastros selecionados.');
    } finally {
      setActionId('');
    }
  };

  const handleCreateMasterWithSelected = async () => {
    if (!selectedExternalRecords.length) return;
    setActionId('create-master-selected');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.createMasterWithSelected({
        externalRecords: buildExternalPayload(),
        fieldSelections: buildFieldSelections()
      });
      if (payload.blocked?.length) {
        setError('Alguns cadastros têm CNPJ diferente. Para eles, crie grupo econômico ou mantenha como clientes separados.');
      }
      await Promise.all([runUnifiedSearch(), loadAll()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar Cliente Mestre com os cadastros selecionados.');
    } finally {
      setActionId('');
    }
  };

  const handleCompleteSelectedMaster = async () => {
    if (!selectedMasterId) return;
    setActionId('complete-master-data');
    setError('');
    try {
      await ClientConsolidationAPI.completeMasterData(selectedMasterId, buildFieldSelections());
      await Promise.all([runUnifiedSearch(), loadAll()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível completar os dados do Cliente Mestre.');
    } finally {
      setActionId('');
    }
  };

  const renderUnifiedRecord = (record, type) => {
    const key = externalKey(record);
    const isMaster = type === 'master';
    const checked = isMaster ? selectedMasterId === record.id : selectedExternalKeys.includes(key);

    return (
      <label className="client-unified-result" key={isMaster ? record.id : key}>
        <input
          type={isMaster ? 'radio' : 'checkbox'}
          name="selectedMasterClient"
          checked={checked}
          onChange={() => (isMaster ? setSelectedMasterId(record.id) : toggleExternalRecord(record))}
        />
        <span>
          <strong>{formatValue(record.name)}</strong>
          <small>{formatValue(record.documentNumber, 'Sem CNPJ')} · {formatValue(record.phone, 'Sem telefone')} · {formatValue(record.email, 'Sem e-mail')}</small>
          {!!record.linkedMasterClientName && <small>Vinculado a: {record.linkedMasterClientName}</small>}
        </span>
      </label>
    );
  };

  const renderCompletionOptions = () => {
    if (!selectedExternalRecords.length) return null;
    const candidates = [];
    for (const record of selectedExternalRecords) {
      for (const [field, label] of COMPLETION_FIELDS) {
        if (!record[field]) continue;
        candidates.push({ field, label, value: record[field], sourceSystem: record.sourceSystem, sourceName: record.name });
      }
    }

    return (
      <div className="client-completion-box">
        <div>
          <div className="eyebrow">Complementação</div>
          <h3>Completar dados do Cliente Mestre</h3>
        </div>
        <div className="client-completion-grid">
          {COMPLETION_FIELDS.map(([field, label]) => {
            const fieldCandidates = candidates.filter((candidate) => candidate.field === field);
            if (!fieldCandidates.length) return null;
            const selected = completionFields[field] || {};

            return (
              <label key={field}>
                <span>
                  <input
                    type="checkbox"
                    checked={Boolean(selected.enabled)}
                    onChange={(event) => setCompletionFields((current) => ({
                      ...current,
                      [field]: {
                        ...(current[field] || fieldCandidates[0]),
                        enabled: event.target.checked
                      }
                    }))}
                  />
                  {label}
                </span>
                <select
                  className="form-select"
                  value={selected.value || ''}
                  onChange={(event) => {
                    const candidate = fieldCandidates.find((item) => item.value === event.target.value) || fieldCandidates[0];
                    setCompletionFields((current) => ({ ...current, [field]: { ...candidate, enabled: true } }));
                  }}
                >
                  <option value="">Escolher valor</option>
                  {fieldCandidates.map((candidate) => (
                    <option key={`${field}-${candidate.sourceSystem}-${candidate.value}`} value={candidate.value}>
                      {candidate.value} ({formatSource(candidate.sourceSystem)})
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
        <button type="button" className="btn btn-outline-primary" disabled={!selectedMasterId || actionId === 'complete-master-data'} onClick={handleCompleteSelectedMaster}>
          Completar dados do Cliente Mestre
        </button>
      </div>
    );
  };

  const tableParams = (tab, page = pages[tab]) => ({
    q: filters.q.trim(),
    status: filters.status,
    origin: filters.origin,
    page,
    limit: PAGE_SIZE
  });

  const loadMasters = async (page = pages.masters) => {
    const payload = await ClientConsolidationAPI.masters(tableParams('masters', page));
    setMastersPayload(payload);
  };

  const loadLinks = async (page = pages.links) => {
    const payload = await ClientConsolidationAPI.links(tableParams('links', page));
    setLinksPayload(payload);
  };

  const loadDocumentMismatchReport = async () => {
    const payload = await ClientConsolidationAPI.documentMismatches();
    setDocumentMismatchReport(payload);
  };

  const loadAll = async ({ showStatus = false } = {}) => {
    setLoading(true);
    setTableLoading(true);
    setShowLoadStatus(showStatus);
    setError('');
    try {
      await Promise.all([loadDashboard(), loadMasters(1), loadLinks(1), loadDocumentMismatchReport()]);
      setPages({ masters: 1, links: 1 });
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar a consolidação de clientes.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      setShowLoadStatus(false);
    }
  };

  const loadActiveTable = async (nextPage = pages[activeTab]) => {
    setTableLoading(true);
    setError('');
    try {
      if (activeTab === 'masters') await loadMasters(nextPage);
      if (activeTab === 'links') await loadLinks(nextPage);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar os dados da aba.');
    } finally {
      setTableLoading(false);
    }
  };

  const reloadAfterMasterMutation = async () => {
    const jobs = [];
    if (activeWorkspaceTab === 'overview') jobs.push(loadDashboard());
    if (activeWorkspaceTab === 'records') {
      if (activeTab === 'masters') jobs.push(loadMasters(pages.masters));
      if (activeTab === 'links') jobs.push(loadLinks(pages.links));
      if (activeTab === 'suggestions') jobs.push(loadDashboard());
      if (activeTab === 'document-mismatch') jobs.push(loadDocumentMismatchReport());
    }
    if (!jobs.length) jobs.push(loadMasters(pages.masters));
    await Promise.all(jobs);
  };

  useEffect(() => {
    if (activeWorkspaceTab !== 'records') return;
    if (activeTab === 'document-mismatch') {
      void loadDocumentMismatchReport();
      return;
    }
    if (activeTab === 'suggestions') {
      void loadDashboard();
      return;
    }
    void loadActiveTable(pages[activeTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeWorkspaceTab]);

  useEffect(() => {
    if (activeWorkspaceTab !== 'overview') return;
    void loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceTab]);

  const openRecordsWorkspace = () => {
    setActiveWorkspaceTab('records');
  };

  const openOverviewWorkspace = () => {
    setActiveWorkspaceTab('overview');
  };

  const openMasterDetail = async (masterClientId) => {
    if (!masterClientId) return;
    setDetailLoading(true);
    setError('');
    try {
      const payload = await ClientConsolidationAPI.auditCandidates(masterClientId);
      setDetail(payload);
      setSelectedAuditCandidateKeys([]);
      setDataComparison(null);
      setComparisonUpdates({});
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível abrir o Cliente Mestre.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    setPages({ masters: 1, links: 1 });
    await loadActiveTable(1);
  };

  const handlePage = async (tab, direction) => {
    const currentPayload = tab === 'masters' ? mastersPayload : linksPayload;
    const nextPage = Math.min(Math.max((pages[tab] || 1) + direction, 1), currentPayload.totalPages || 1);
    setPages((current) => ({ ...current, [tab]: nextPage }));
    setTableLoading(true);
    setError('');
    try {
      if (tab === 'masters') await loadMasters(nextPage);
      if (tab === 'links') await loadLinks(nextPage);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível trocar de página.');
    } finally {
      setTableLoading(false);
    }
  };

  const handleReprocess = async () => {
    setLoading(true);
    setTableLoading(true);
    setShowLoadStatus(true);
    setError('');
    try {
      const payload = await ClientConsolidationAPI.reprocess();
      setSummary(payload.summary || {});
      setSuggestions(payload.suggestions || []);
      await Promise.all([loadMasters(1), loadLinks(1), loadDocumentMismatchReport()]);
      setPages({ masters: 1, links: 1 });
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível reprocessar as sugestões.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      setShowLoadStatus(false);
    }
  };

  const handleBackfillNormalizedData = async () => {
    setLoading(true);
    setTableLoading(true);
    setShowLoadStatus(true);
    setError('');
    setBackfillResult(null);
    try {
      const payload = await ClientConsolidationAPI.backfillAllNormalizedData();
      setBackfillResult(payload);
      await loadAll();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível normalizar os dados importados.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      setShowLoadStatus(false);
    }
  };

  const runSuggestionAction = async (suggestion, action) => {
    setActionId(suggestion.id);
    setError('');
    try {
      if (action === 'approve') await ClientConsolidationAPI.approve(suggestion.id);
      if (action === 'ignore') await ClientConsolidationAPI.ignore(suggestion.id);
      if (action === 'create-master') await ClientConsolidationAPI.createMaster(suggestion.externalCustomerId);
      await loadAll();
    } catch (requestError) {
      handleApiError(requestError, 'Não consegui executar a ação nesta sugestão.');
    } finally {
      setActionId('');
    }
  };

  const handleUnlink = async (linkId) => {
    if (!linkId || !detail?.masterClient?._id) return;
    setActionId(linkId);
    setError('');
    try {
      await ClientConsolidationAPI.unlink(linkId);
      await openMasterDetail(detail.masterClient._id);
      await reloadAfterMasterMutation();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível desvincular o cadastro.');
    } finally {
      setActionId('');
    }
  };

  const handleDeleteMaster = async () => {
    const master = detail?.masterClient;
    if (!master?._id) return;
    const linkCount = detail?.linkedRecords?.length || 0;
    const confirmed = window.confirm(`Apagar o Cliente Mestre "${formatValue(master.name)}" e ${linkCount} vínculo(s)? Os cadastros originais do Bling/Auvo serão mantidos.`);
    if (!confirmed) return;

    setActionId(`delete-master-${master._id}`);
    setError('');
    try {
      await ClientConsolidationAPI.deleteMaster(master._id);
      setDetail(null);
      setSelectedAuditCandidateKeys([]);
      setDataComparison(null);
      await reloadAfterMasterMutation();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível apagar o Cliente Mestre duplicado.');
    } finally {
      setActionId('');
    }
  };

  const handleMergeMasterInto = async (targetMaster) => {
    if (!mergeSourceMaster?.id || !targetMaster?.id || mergeSourceMaster.id === targetMaster.id) return;
    const confirmed = window.confirm(`Mesclar "${mergeSourceMaster.name}" em "${targetMaster.name}"? O mestre de origem ficará inativo e seus vínculos serão movidos para o destino.`);
    if (!confirmed) return;

    setActionId(`merge-${mergeSourceMaster.id}-${targetMaster.id}`);
    setError('');
    try {
      await ClientConsolidationAPI.mergeMasters({
        sourceMasterClientId: mergeSourceMaster.id,
        targetMasterClientId: targetMaster.id
      });
      setMergeSourceMaster(null);
      await reloadAfterMasterMutation();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível mesclar os Clientes Mestres. Confira se os CNPJs são compatíveis.');
    } finally {
      setActionId('');
    }
  };

  const handleMasterMergeButton = (event, master) => {
    event.stopPropagation();
    if (!mergeSourceMaster) {
      setMergeSourceMaster(master);
      return;
    }
    if (mergeSourceMaster.id === master.id) {
      setMergeSourceMaster(null);
      return;
    }
    void handleMergeMasterInto(master);
  };

  const auditCandidateKey = (candidate) => `${candidate.sourceSystem}:${candidate.importedClientId}:${candidate.externalId || ''}`;

  const toggleAuditCandidate = (candidate) => {
    const key = auditCandidateKey(candidate);
    setSelectedAuditCandidateKeys((current) => (
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    ));
  };

  const selectedAuditCandidates = () => [
    ...(detail?.blingCandidates || []),
    ...(detail?.auvoCandidates || [])
  ].filter((candidate) => selectedAuditCandidateKeys.includes(auditCandidateKey(candidate)));

  const handleLinkAuditCandidates = async () => {
    if (!detail?.masterClient?._id) return;
    const selectedCandidates = selectedAuditCandidates();
    if (!selectedCandidates.length) return;
    if (selectedCandidates.some((candidate) => candidate.warning?.includes('CNPJ diferente'))) {
      setError('CNPJ diferente. Pode ser matriz/filial ou grupo econômico. Não vincular automaticamente.');
      return;
    }

    setActionId('link-audit-candidates');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.linkCandidates(detail.masterClient._id, selectedCandidates.map((candidate) => ({
        sourceSystem: candidate.sourceSystem,
        importedClientId: candidate.importedClientId,
        externalId: candidate.externalId
      })));
      if (payload.blocked?.length) {
        setError('CNPJ diferente. Pode ser matriz/filial ou grupo econômico. Não vincular automaticamente.');
      }
      await openMasterDetail(detail.masterClient._id);
      await Promise.all([loadMasters(pages.masters), loadLinks(pages.links), loadDashboard()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível vincular os candidatos selecionados.');
    } finally {
      setActionId('');
    }
  };

  const handleCreateMasterFromAuditSelected = async () => {
    const selectedCandidates = selectedAuditCandidates();
    if (!selectedCandidates.length) return;
    setActionId('create-master-from-audit');
    setError('');
    try {
      await ClientConsolidationAPI.createMasterWithSelected({
        externalRecords: selectedCandidates.map((candidate) => ({
          sourceSystem: candidate.sourceSystem,
          importedClientId: candidate.importedClientId,
          externalId: candidate.externalId
        }))
      });
      if (detail?.masterClient?._id) await openMasterDetail(detail.masterClient._id);
      await loadAll();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar novo Cliente Mestre com os candidatos selecionados.');
    } finally {
      setActionId('');
    }
  };

  const handleOpenDataComparison = async () => {
    if (!detail?.masterClient?._id) return;
    setActionId('data-comparison');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.dataComparison(detail.masterClient._id);
      setDataComparison(payload);
      const suggested = {};
      for (const item of payload.suggestedUpdates || []) {
        suggested[item.field] = item.suggestedValue;
      }
      setComparisonUpdates(suggested);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível abrir a comparação de dados.');
    } finally {
      setActionId('');
    }
  };

  const handleApplyDataComparison = async () => {
    if (!detail?.masterClient?._id) return;
    const updates = Object.fromEntries(
      Object.entries(comparisonUpdates).filter(([, value]) => String(value || '').trim())
    );
    setActionId('apply-data-comparison');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.applyDataCompletion(detail.masterClient._id, updates);
      const refreshedComparison = await ClientConsolidationAPI.dataComparison(detail.masterClient._id);
      setDetail((current) => current ? { ...current, masterClient: payload.masterClient || current.masterClient } : current);
      setDataComparison(refreshedComparison);
      setComparisonUpdates({});
      await reloadAfterMasterMutation();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível completar os dados do Cliente Mestre.');
    } finally {
      setActionId('');
    }
  };

  const firstSourceValue = (records, field) => records?.map((record) => record[field]).find((value) => String(value || '').trim()) || '';

  const renderDataComparison = () => {
    if (!dataComparison) return null;
    const conflictFields = new Set((dataComparison.conflicts || []).map((item) => item.field));
    const suggestionFields = new Set((dataComparison.suggestedUpdates || []).map((item) => item.field));

    return (
      <div className="client-detail-section mt-3">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <h3>Comparativo para completar dados</h3>
          <button type="button" className="btn btn-sm btn-primary" disabled={actionId === 'apply-data-comparison'} onClick={handleApplyDataComparison}>
            Aplicar valores escolhidos
          </button>
        </div>
        <table className="client-audit-table client-audit-table--compact">
          <thead>
            <tr>
              <th>Campo</th>
              <th>Cliente Mestre</th>
              <th>Bling</th>
              <th>Auvo</th>
              <th>Valor escolhido</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON_FIELDS.map(([field, label]) => {
              const masterValue = dataComparison.masterClient?.[field] || '';
              const blingValue = firstSourceValue(dataComparison.blingRecords, field);
              const auvoValue = firstSourceValue(dataComparison.auvoRecords, field);
              const options = [...new Set([masterValue, blingValue, auvoValue].filter(Boolean))];

              return (
                <tr key={field} className={conflictFields.has(field) ? 'client-row-warning' : suggestionFields.has(field) ? 'client-row-success' : ''}>
                  <td>{label}</td>
                  <td>{formatValue(masterValue, 'vazio')}</td>
                  <td>{formatValue(blingValue, 'vazio')}</td>
                  <td>{formatValue(auvoValue, 'vazio')}</td>
                  <td>
                    <select
                      className="form-select"
                      value={comparisonUpdates[field] || ''}
                      onChange={(event) => setComparisonUpdates((current) => ({ ...current, [field]: event.target.value }))}
                    >
                      <option value="">Manter atual</option>
                      {options.map((value) => <option key={`${field}-${value}`} value={value}>{value}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCandidateTable = (title, candidates = []) => (
    <div className="client-detail-section mt-3">
      <h3>{title}</h3>
      <table className="client-audit-table client-audit-table--compact">
        <thead>
          <tr>
            <th></th>
            <th>Nome</th>
            <th>Razão social</th>
            <th>Fantasia</th>
            <th>CNPJ</th>
            <th>Telefone</th>
            <th>E-mail</th>
            <th>Endereço</th>
            <th>Cidade</th>
            <th>ID</th>
            <th>Status</th>
            <th>Score</th>
            <th>Motivos</th>
          </tr>
        </thead>
        <tbody>
          {!candidates.length && <tr><td colSpan="13">Nenhum candidato encontrado.</td></tr>}
          {candidates.map((candidate) => {
            const key = auditCandidateKey(candidate);
            const hasDocumentWarning = candidate.warning?.includes('CNPJ diferente');
            return (
              <tr key={key} className={hasDocumentWarning ? 'client-row-warning' : candidate.confidenceScore >= 95 ? 'client-row-success' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedAuditCandidateKeys.includes(key)}
                    disabled={candidate.alreadyLinked || hasDocumentWarning}
                    onChange={() => toggleAuditCandidate(candidate)}
                  />
                </td>
                <td>{formatValue(candidate.name)}</td>
                <td>{formatValue(candidate.corporateName)}</td>
                <td>{formatValue(candidate.fantasyName)}</td>
                <td>{formatValue(candidate.documentNumber)}</td>
                <td>{formatValue(candidate.phone)}</td>
                <td>{formatValue(candidate.email)}</td>
                <td>{formatValue(candidate.address)}</td>
                <td>{formatValue(candidate.city)}</td>
                <td>{formatValue(candidate.externalId)}</td>
                <td>{candidate.alreadyLinked ? 'Já vinculado' : hasDocumentWarning ? 'CNPJ diferente' : 'Possível vínculo'}</td>
                <td>{candidate.confidenceScore || 0}%</td>
                <td>
                  {candidate.matchReasons?.length ? candidate.matchReasons.join(', ') : 'Sem motivo'}
                  {candidate.warning && <div className="client-warning-text">{candidate.warning}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderPager = (tab, payload) => (
    <div className="client-table-pager">
      <span>{payload.total || 0} registros · página {pages[tab] || 1} de {payload.totalPages || 1}</span>
      <div>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(pages[tab] || 1) <= 1 || tableLoading} onClick={() => handlePage(tab, -1)}>
          Anterior
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(pages[tab] || 1) >= (payload.totalPages || 1) || tableLoading} onClick={() => handlePage(tab, 1)}>
          Próxima
        </button>
      </div>
    </div>
  );

  const ShellTag = pageMode ? 'main' : 'aside';
  const operationTitle = unifiedLoading
    ? 'Buscando clientes no TaskHub, Bling e AUVO'
    : loading && showLoadStatus
      ? 'Atualizando consolidação de clientes'
      : detailLoading
        ? 'Abrindo auditoria do cliente'
        : '';

  return (
    <ShellTag className={`${pageMode ? 'app-shell client-consolidation-page' : 'task-overlay-panel task-overlay-panel--wide'} client-consolidation-panel`}>
      <div className="overlay-panel-header">
        <div>
          <div className="eyebrow">Cadastro Mestre</div>
          <h2>Consolidação de Clientes</h2>
          <p className="text-muted mb-0">Audite vínculos entre cadastros importados e Clientes Mestres do Task Hub.</p>
        </div>
        {onClose && <button type="button" className="btn btn-outline-secondary" onClick={onClose}>{pageMode ? 'Voltar' : 'Fechar'}</button>}
      </div>

      <OperationStatus
        active={Boolean(operationTitle)}
        title={operationTitle}
        steps={[
          unifiedLoading && 'Pesquisando Cliente Mestre e fontes integradas.',
          loading && showLoadStatus && 'Consultando o backend do TaskHub e o cec_api_integra.',
          loading && showLoadStatus && 'Atualizando paginação, vínculos e indicadores.',
          detailLoading && 'Carregando candidatos e vínculos para conferência.',
        ]}
      />

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      {backfillResult && (
        <div className="alert alert-success mt-3 mb-0">
          Normalização concluída: {backfillResult.customersUpdated || 0} clientes, {backfillResult.linksUpdated || 0} vínculos e {backfillResult.masterClientsUpdated || 0} Clientes Mestres atualizados.
        </div>
      )}

      <div className="service-workspace-tabs mt-3" role="tablist" aria-label="Área de clientes">
        <button type="button" className={activeWorkspaceTab === 'consolidate' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('consolidate')}>
          Consolidar cadastros
        </button>
        <button type="button" className={activeWorkspaceTab === 'records' ? 'active' : ''} onClick={openRecordsWorkspace}>
          Clientes e vínculos
        </button>
        <button type="button" className={activeWorkspaceTab === 'overview' ? 'active' : ''} onClick={openOverviewWorkspace}>
          Indicadores
        </button>
      </div>

      {activeWorkspaceTab === 'overview' && (
      <section className="service-workspace-panel">
      <div className="service-workspace-heading mt-3">
        <div>
          <div className="eyebrow">Indicadores</div>
          <h3>Visão de clientes</h3>
        </div>
        <div className="overlay-actions">
          <button type="button" className="btn btn-sm btn-success" onClick={() => { window.location.href = '/sync-center'; }} disabled={loading}>
            Abrir Central
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={handleReprocess} disabled={loading}>
            Reprocessar
          </button>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleBackfillNormalizedData} disabled={loading}>
            Normalizar
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadAll({ showStatus: true })} disabled={loading}>
            Atualizar
          </button>
        </div>
      </div>
      <div className="client-dashboard">
        <div className="client-total-card">
          <span>Clientes Totais Encontrados</span>
          <strong>{dashboard.totalClients || summary.importedTotal || 0}</strong>
          <small>clientes</small>
        </div>

        <div className="client-dashboard-section">
          <h3>Distribuição por origem</h3>
          <div className="client-metric-list">
            <div><span>Bling</span><strong>{sourceDistribution.bling || 0}</strong></div>
            <div><span>Auvo</span><strong>{sourceDistribution.auvo || 0}</strong></div>
            <div><span>Clientes integrados</span><strong>{sourceDistribution.cecIntegrated || 0}</strong></div>
            <div><span>Manual</span><strong>{sourceDistribution.manual || 0}</strong></div>
            <div><span>Task Hub</span><strong>{sourceDistribution.taskhub || 0}</strong></div>
          </div>
          <div className="client-dashboard-note">
            Referências dentro dos cadastros integrados: Bling {sourceReferences.bling || 0}, Auvo {sourceReferences.auvo || 0}.
          </div>
        </div>

        <div className="client-dashboard-section">
          <h3>Situação da Consolidação</h3>
          <div className="client-metric-list">
            <div><span>Integrados ao Cliente Mestre</span><strong>{consolidation.integratedToMaster || 0}</strong></div>
            <div><span>Pendentes de Consolidação</span><strong>{consolidation.pendingConsolidation || 0}</strong></div>
            <div><span>Ignorados</span><strong>{consolidation.ignored || 0}</strong></div>
            <div><span>Sugestões de Duplicidade</span><strong>{consolidation.duplicateSuggestions || summary.possibleDuplicates || 0}</strong></div>
          </div>
        </div>

        <div className="client-dashboard-section">
          <h3>Qualidade dos dados</h3>
          <div className="client-metric-list">
            <div><span>Clientes com CNPJ</span><strong>{dataQuality.withDocument || 0}</strong></div>
            <div><span>Clientes sem CNPJ</span><strong>{dataQuality.withoutDocument || 0}</strong></div>
            <div><span>Clientes com e-mail</span><strong>{dataQuality.withEmail || 0}</strong></div>
            <div><span>Clientes sem e-mail</span><strong>{dataQuality.withoutEmail || 0}</strong></div>
            <div><span>Clientes com telefone</span><strong>{dataQuality.withPhone || 0}</strong></div>
            <div><span>Clientes sem telefone</span><strong>{dataQuality.withoutPhone || 0}</strong></div>
          </div>
        </div>

        <div className="client-rate-grid">
          <div className="client-rate-card">
            <span>Taxa de Consolidação</span>
            <strong>{formatPercent(rates.consolidationRate)}</strong>
          </div>
          <div className="client-rate-card">
            <span>Taxa de Qualidade dos Dados</span>
            <strong>{formatPercent(rates.dataQualityRate)}</strong>
            <small>{dataQuality.completeRecords || 0} clientes com CNPJ, telefone e e-mail</small>
          </div>
        </div>
        <div className="client-dashboard-section client-dashboard-section--attention">
          <h3>Saneamento da integração</h3>
          <div className="client-metric-list">
            <div><span>Bling sem Auvo</span><strong>{sourceCoverage.onlyBling || 0}</strong></div>
            <div><span>Auvo sem Bling</span><strong>{sourceCoverage.onlyAuvo || 0}</strong></div>
            <div><span>Status divergente</span><strong>{sourceCoverage.statusDivergences || 0}</strong></div>
            <div><span>Inativos nas origens</span><strong>{sourceHealth.inactive || 0}</strong></div>
            <div><span>Removidos nas origens</span><strong>{sourceHealth.removed || 0}</strong></div>
          </div>
        </div>
      </div>
      </section>
      )}

      {activeWorkspaceTab === 'records' && (
      <section className="service-workspace-panel">
      <div className="service-workspace-heading mt-3">
        <div>
          <div className="eyebrow">Cadastros</div>
          <h3>Clientes consolidados e vínculos</h3>
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadAll({ showStatus: true })} disabled={loading}>
          Atualizar
        </button>
      </div>
      <form className="client-audit-filters mt-4" onSubmit={handleApplyFilters}>
        <div className="filter-control">
          <label className="form-label">Busca</label>
          <input
            className="form-control"
            value={filters.q}
            onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
            placeholder="Nome, CNPJ, telefone ou e-mail"
          />
        </div>
        <div className="filter-control">
          <label className="form-label">Situação</label>
          <select className="form-select" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
            <option value="">Todas</option>
            <option value="integrated">Integrado</option>
            <option value="pending">Pendente</option>
            <option value="ignored">Ignorado</option>
          </select>
        </div>
        <div className="filter-control">
          <label className="form-label">Origem</label>
          <select className="form-select" value={filters.origin} onChange={(event) => setFilters((current) => ({ ...current, origin: event.target.value }))}>
            <option value="">Todas</option>
            <option value="bling">Bling</option>
            <option value="auvo">Auvo</option>
            <option value="manual">Manual</option>
            <option value="cec_integrated">Clientes integrados</option>
            <option value="taskhub">Task Hub</option>
          </select>
        </div>
        <button type="submit" className="btn btn-dark" disabled={tableLoading}>Aplicar filtros</button>
      </form>

      <div className="task-tabs mt-4">
        <button type="button" className={`task-tab ${activeTab === 'masters' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('masters')}>Clientes Consolidados</button>
        <button type="button" className={`task-tab ${activeTab === 'links' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('links')}>Vínculos</button>
        <button type="button" className={`task-tab ${activeTab === 'document-mismatch' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('document-mismatch')}>CNPJ divergente</button>
        <button type="button" className={`task-tab ${activeTab === 'suggestions' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('suggestions')}>Sugestões</button>
      </div>

      {activeTab === 'masters' && (
        <div className="client-table-wrap mt-3">
          {mergeSourceMaster && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
              <div>
                <strong>{formatValue(mergeSourceMaster.name)}</strong> será mesclado no Cliente Mestre escolhido como destino.
                <div className="small">Clique em “Mesclar aqui” na linha que deve permanecer.</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMergeSourceMaster(null)}>
                Cancelar mesclagem
              </button>
            </div>
          )}
          {renderPager('masters', mastersPayload)}
          <table className="client-audit-table">
            <thead>
              <tr>
                <th>Cliente Mestre</th>
                <th>CNPJ</th>
                <th>Cidade</th>
                <th>Estado</th>
                <th>Vínculos</th>
                <th>Origens vinculadas</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading && <tr><td colSpan="8">Carregando clientes...</td></tr>}
              {!tableLoading && !mastersPayload.rows?.length && <tr><td colSpan="8">Nenhum Cliente Mestre encontrado.</td></tr>}
              {!tableLoading && mastersPayload.rows?.map((master) => (
                <tr key={master.id} onClick={() => openMasterDetail(master.id)}>
                  <td><strong>{formatValue(master.name)}</strong><small>{formatValue(master.email, '')}</small></td>
                  <td>{formatValue(master.documentNumber)}</td>
                  <td>{formatValue(master.city)}</td>
                  <td>{formatValue(master.state)}</td>
                  <td>{master.linkCount || 0}</td>
                  <td>{originSummary(master.linkedOrigins)}</td>
                  <td>{master.status === 'active' ? 'Ativo' : 'Inativo'}</td>
                  <td>
                    <button
                      type="button"
                      className={`btn btn-sm ${mergeSourceMaster?.id && mergeSourceMaster.id !== master.id ? 'btn-primary' : 'btn-outline-primary'}`}
                      disabled={Boolean(actionId)}
                      onClick={(event) => handleMasterMergeButton(event, master)}
                    >
                      {mergeSourceMaster?.id === master.id ? 'Cancelar' : mergeSourceMaster?.id ? 'Mesclar aqui' : 'Mesclar em outro'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPager('masters', mastersPayload)}
        </div>
      )}

      {activeTab === 'links' && (
        <div className="client-table-wrap mt-3">
          {renderPager('links', linksPayload)}
          <table className="client-audit-table">
            <thead>
              <tr>
                <th>Cliente Mestre</th>
                <th>Sistema origem</th>
                <th>ID externo</th>
                <th>Nome original</th>
                <th>CNPJ original</th>
                <th>Telefone original</th>
                <th>E-mail original</th>
                <th>Motivo</th>
                <th>Confiança</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading && <tr><td colSpan="9">Carregando vínculos...</td></tr>}
              {!tableLoading && !linksPayload.rows?.length && <tr><td colSpan="9">Nenhum vínculo encontrado.</td></tr>}
              {!tableLoading && linksPayload.rows?.map((link) => (
                <tr key={link.id} onClick={() => openMasterDetail(masterIdFromLink(link))}>
                  <td>{formatValue(link.masterClient?.name)}</td>
                  <td>{formatSource(link.sourceSystem)}</td>
                  <td>{formatValue(link.externalId)}</td>
                  <td>{formatValue(link.externalName)}</td>
                  <td>{formatValue(link.documentNumber)}</td>
                  <td>{formatValue(link.phone)}</td>
                  <td>{formatValue(link.email)}</td>
                  <td>{link.reasons?.length ? link.reasons.join(', ') : 'Sem motivo registrado'}</td>
                  <td>{link.confidenceScore || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPager('links', linksPayload)}
        </div>
      )}

      {activeTab === 'document-mismatch' && (
        <div className="client-table-wrap mt-3">
          <div className={documentMismatchReport.total ? 'alert alert-danger mb-3' : 'alert alert-success mb-3'}>
            Relatório: Clientes consolidados com CNPJ diferente. Total encontrado: {documentMismatchReport.total || 0}.
          </div>
          <table className="client-audit-table">
            <thead>
              <tr>
                <th>Cliente Mestre</th>
                <th>CNPJ Mestre</th>
                <th>Origem</th>
                <th>ID externo</th>
                <th>Nome original</th>
                <th>CNPJ vinculado</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {!documentMismatchReport.rows?.length && (
                <tr><td colSpan="7">Nenhum vínculo com CNPJ divergente encontrado.</td></tr>
              )}
              {documentMismatchReport.rows?.map((row) => (
                <tr key={row.id} onClick={() => openMasterDetail(row.masterClient?._id)}>
                  <td>{formatValue(row.masterClient?.name)}</td>
                  <td>{formatValue(row.masterDocumentNumber)}</td>
                  <td>{formatSource(row.sourceSystem)}</td>
                  <td>{formatValue(row.externalId)}</td>
                  <td>{formatValue(row.externalName)}</td>
                  <td>{formatValue(row.linkedDocumentNumber)}</td>
                  <td>{row.confidenceScore || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'suggestions' && (
        <div className="client-suggestion-list mt-4">
          <div className="client-list-heading">
            <div>
              <div className="eyebrow">Sugestões</div>
              <h3>Possíveis vínculos entre cadastros</h3>
            </div>
            <span>{orderedSuggestions.length} pendentes</span>
          </div>

          {!orderedSuggestions.length && (
            <div className="empty-state">Nenhuma sugestão de duplicidade pendente encontrada.</div>
          )}

          {orderedSuggestions.map((suggestion) => {
            const master = suggestion.masterClient || {};
            const disabled = actionId === suggestion.id;
            const isEconomicGroup = suggestion.decision === 'economic_group' || suggestion.documentMismatch;

            return (
              <article className="client-suggestion-card" key={suggestion.id}>
                <div className="client-suggestion-card__main">
                  <div>
                    <span className="client-suggestion-label">Cliente Mestre sugerido</span>
                    <h3>{formatValue(master.name)}</h3>
                    <p>{formatValue(master.documentNumber)} · {formatValue(master.email)} · {formatValue(master.phone)}</p>
                  </div>
                  <div>
                    <span className="client-suggestion-label">Cadastro externo encontrado</span>
                    <h3>{formatValue(suggestion.externalName)}</h3>
                    <p>{formatSource(suggestion.sourceSystem)} · {formatValue(suggestion.documentNumber)} · {formatValue(suggestion.phone)} · {formatValue(suggestion.email)}</p>
                  </div>
                  <div className={`client-confidence ${isEconomicGroup ? '' : suggestion.confidenceScore >= 95 ? 'client-confidence--auto' : 'client-confidence--review'}`}>
                    <strong>{suggestion.confidenceScore}%</strong>
                    <span>{isEconomicGroup ? 'Grupo econômico' : suggestion.confidenceScore >= 95 ? 'Automático' : 'Revisão'}</span>
                  </div>
                </div>

                {!!suggestion.reasons?.length && (
                  <div className="client-reasons">
                    {suggestion.reasons.map((reason) => <span key={reason}>{reason}</span>)}
                  </div>
                )}

                <div className="client-suggestion-actions">
                  <button type="button" className="btn btn-sm btn-primary" disabled={disabled || isEconomicGroup} onClick={() => runSuggestionAction(suggestion, 'approve')}>
                    Aprovar vínculo
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => runSuggestionAction(suggestion, 'ignore')}>
                    Ignorar
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => runSuggestionAction(suggestion, 'create-master')}>
                    Criar novo Cliente Mestre
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => openMasterDetail(master._id)}>
                    Ver Cliente Mestre
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      </section>
      )}

      {activeWorkspaceTab === 'consolidate' && (
      <section className="client-unified-search mt-4">
        <form className="client-unified-search__bar" onSubmit={runUnifiedSearch}>
          <div>
            <div className="eyebrow">Busca unificada</div>
            <h3>Pesquisar Cliente Mestre, Bling e Auvo</h3>
          </div>
          <input
            className="form-control"
            value={unifiedQuery}
            onChange={(event) => setUnifiedQuery(event.target.value)}
            placeholder="Ex.: geolab"
          />
          <button type="submit" className="btn btn-dark" disabled={unifiedLoading}>Buscar</button>
        </form>
        <OperationStatus
          active={unifiedLoading}
          title="Busca unificada em andamento"
          steps={['Pesquisando Cliente Mestre.', 'Consultando dados consolidados do Bling e AUVO.', 'Preparando resultados para vínculo.']}
        />

        {!!unifiedQuery.trim() && (
          <div className="client-unified-grid mt-3">
            <div className="client-unified-block">
              <h3>Cliente Mestre</h3>
              {!unifiedResults.masterClients?.length && <div className="empty-state">Nenhum Cliente Mestre encontrado.</div>}
              {unifiedResults.masterClients?.map((record) => renderUnifiedRecord(record, 'master'))}
            </div>
            <div className="client-unified-block">
              <h3>Cadastros Bling</h3>
              {!unifiedResults.blingClients?.length && <div className="empty-state">Nenhum cadastro Bling encontrado.</div>}
              {unifiedResults.blingClients?.map((record) => renderUnifiedRecord(record, 'external'))}
            </div>
            <div className="client-unified-block">
              <h3>Cadastros Auvo</h3>
              {!unifiedResults.auvoClients?.length && <div className="empty-state">Nenhum cadastro Auvo encontrado.</div>}
              {unifiedResults.auvoClients?.map((record) => renderUnifiedRecord(record, 'external'))}
            </div>
            <div className="client-unified-block">
              <h3>Vínculos existentes</h3>
              {!unifiedResults.linkedRecords?.length && <div className="empty-state">Nenhum vínculo existente encontrado.</div>}
              {unifiedResults.linkedRecords?.map((record) => (
                <button type="button" className="client-existing-link" key={record.id} onClick={() => openMasterDetail(record.linkedMasterClientId)}>
                  <strong>{formatSource(record.sourceSystem)} · {formatValue(record.name)}</strong>
                  <span>{formatValue(record.documentNumber, 'Sem CNPJ')} · Mestre: {formatValue(record.linkedMasterClientName)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!!selectedExternalRecords.length && (
          <div className="client-selected-actions mt-3">
            {detectDocumentMismatch() && (
              <div className="alert alert-warning mb-0">
                Os CNPJs são diferentes. Deseja criar grupo econômico ou manter como clientes separados?
              </div>
            )}
            <div className="overlay-actions">
              <button type="button" className="btn btn-primary" disabled={!selectedMasterId || actionId === 'link-selected' || detectDocumentMismatch()} onClick={handleLinkSelected}>
                Vincular selecionados
              </button>
              <button type="button" className="btn btn-outline-primary" disabled={actionId === 'create-master-selected'} onClick={handleCreateMasterWithSelected}>
                Criar Cliente Mestre com selecionados
              </button>
            </div>
            {renderCompletionOptions()}
          </div>
        )}
      </section>
      )}

      {detail && (
        <div className="client-detail-rail">
          <aside className="task-overlay-panel client-detail-drawer shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Auditoria</div>
                <h2>{formatValue(detail.masterClient?.name)}</h2>
                <p className="text-muted mb-0">
                  {formatValue(detail.masterClient?.documentNumber, 'Sem CNPJ')} · {detail.masterClient?.status === 'active' ? 'Ativo' : 'Inativo'}
                </p>
              </div>
              <div className="overlay-actions">
                <button type="button" className="btn btn-primary" disabled={!selectedAuditCandidateKeys.length || actionId === 'link-audit-candidates'} onClick={handleLinkAuditCandidates}>
                  Vincular selecionados
                </button>
                <button type="button" className="btn btn-outline-primary" disabled={!selectedAuditCandidateKeys.length || actionId === 'create-master-from-audit'} onClick={handleCreateMasterFromAuditSelected}>
                  Criar novo Cliente Mestre
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDetail(null)}>Fechar</button>
              </div>
            </div>

            {detailLoading && <div className="empty-state mt-3">Carregando detalhes...</div>}

            <div className="client-detail-section mt-3">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <h3>Cliente Mestre</h3>
                <div className="overlay-actions">
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={actionId === 'data-comparison'} onClick={handleOpenDataComparison}>
                    Completar dados do Cliente Mestre
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => setError('Edição de Cliente Mestre deve ser aberta em ação separada.')}>
                    Editar dados do Cliente Mestre
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" disabled={actionId === `delete-master-${detail.masterClient?._id}`} onClick={handleDeleteMaster}>
                    {actionId === `delete-master-${detail.masterClient?._id}` ? 'Apagando...' : 'Apagar duplicado'}
                  </button>
                </div>
              </div>
              <div className="client-readonly-grid">
                <div><span>Nome</span><strong>{formatValue(detail.masterClient?.name)}</strong></div>
                <div><span>Razão social</span><strong>{formatValue(detail.masterClient?.corporateName)}</strong></div>
                <div><span>Nome fantasia</span><strong>{formatValue(detail.masterClient?.fantasyName)}</strong></div>
                <div><span>CNPJ</span><strong>{formatValue(detail.masterClient?.documentNumber)}</strong></div>
                <div><span>Inscrição estadual</span><strong>{formatValue(detail.masterClient?.stateRegistration)}</strong></div>
                <div><span>Telefone</span><strong>{formatValue(detail.masterClient?.phone)}</strong></div>
                <div><span>Celular</span><strong>{formatValue(detail.masterClient?.mobile)}</strong></div>
                <div><span>E-mail</span><strong>{formatValue(detail.masterClient?.email)}</strong></div>
                <div><span>Endereço</span><strong>{formatValue(detail.masterClient?.address || detail.masterClient?.metadata?.address)}</strong></div>
                <div><span>Número</span><strong>{formatValue(detail.masterClient?.number)}</strong></div>
                <div><span>Bairro</span><strong>{formatValue(detail.masterClient?.district)}</strong></div>
                <div><span>Cidade</span><strong>{formatValue(detail.masterClient?.city)}</strong></div>
                <div><span>Estado</span><strong>{formatValue(detail.masterClient?.state)}</strong></div>
                <div><span>CEP</span><strong>{formatValue(detail.masterClient?.zipCode)}</strong></div>
                <div><span>Origem principal</span><strong>{formatSource(detail.masterClient?.primarySourceSystem)}</strong></div>
              </div>
            </div>

            {renderDataComparison()}

            {renderCandidateTable('Possíveis cadastros do Bling', detail.blingCandidates || [])}
            {renderCandidateTable('Possíveis cadastros do Auvo', detail.auvoCandidates || [])}

            <div className="client-detail-section mt-3">
              <h3>Cadastros já vinculados</h3>
              <div className="client-linked-list">
                {!detail.linkedRecords?.length && <div className="empty-state">Nenhum cadastro vinculado.</div>}
                {detail.linkedRecords?.map((link) => (
                  <article key={link._id} className="client-linked-card">
                    <div>
                      <strong>{formatSource(link.sourceSystem)} · {formatValue(link.externalName)}</strong>
                      <span>ID {formatValue(link.externalId)} · CNPJ {formatValue(link.documentNumber)} · {formatValue(link.phone)} · {formatValue(link.email)}</span>
                      <span>Motivo: {link.reasons?.length ? link.reasons.join(', ') : 'Sem motivo registrado'}</span>
                      <span>Confiança {link.confidenceScore || 0}% · Vinculado em {formatDate(link.linkedAt)}</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={actionId === link._id} onClick={() => handleUnlink(link._id)}>
                      Desvincular cadastro
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </aside>
        </div>
      )}
    </ShellTag>
  );
}
